import {
    Avatar,
    Box,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import DoneIcon from '@mui/icons-material/Done'
import { useDispatch, useSelector } from 'react-redux'
import styles from '../index.module.scss'
import {
    BookingConfig,
    makeSelectHasMobileViewedBooking,
} from '../../redux/schoolBookingsSlice'
import { ReactNode, useEffect, useMemo, useRef } from 'react'
import { useSnackbar } from 'notistack'
import { pushModal } from '../../redux/schoolModalSlice'
import { useTeacherPublicInfoField } from '../../pages/Dashboard/schools/hooks'
import { RootState } from '../../redux/store'
import { selectUserUid } from '../../redux/userSlice'
import { SCHOOLS_API } from '../../firebase/school-api'
import { SchoolEventEntity } from '../../redux/schoolEventsSlice'
import { selectActiveOrgId } from '../../redux/orgSlice'
import { reverseDate } from '../../pages/Dashboard/schools/BookingsPage/helper'
import { logger } from '../../helpers/logger'

export type SchoolEventType =
    | 'BOOKING_CANCELLED_BY_TEACHER'
    | 'BOOKING_CANCELLED_BY_SCHOOL'
    | 'BOOKING_CANCELLED_REPLACING'
    | 'BOOKING_REPLACED'
    // This event shouldn't happen if the school user is confirming the booking
    // Only if another school user confirms booking
    | 'BOOKING_CONFIRMED'
export type SchoolEvent = Omit<BookingConfig, 'isSupportStaff'> & {
    author?: {
        uid: string
        name: string
    }
    type: SchoolEventType
    timeStamp: number
}

export const notificationItemMap = (
    firstName?: string
): Record<
    SchoolEventType,
    {
        title: string
        subtitle: string
        badgeIcon: ReactNode
        badgeColor: string
    }
> => ({
    BOOKING_CANCELLED_BY_SCHOOL: {
        title: 'Booking Cancelled',
        subtitle: `This booking was cancelled by ${firstName}`,
        badgeIcon: <CloseIcon />,
        badgeColor: styles.destructiveRed500,
    },
    BOOKING_CANCELLED_BY_TEACHER: {
        title: 'Booking Cancelled',
        subtitle: `This booking was cancelled by ${firstName}`,
        badgeIcon: <CloseIcon />,
        badgeColor: styles.destructiveRed500,
    },
    BOOKING_CANCELLED_REPLACING: {
        title: 'Booking Cancelled',
        subtitle: `Spark is looking for a replacement`,
        badgeIcon: <SearchIcon />,
        badgeColor: styles.primaryPurple500,
    },
    BOOKING_REPLACED: {
        title: 'Replacement Confirmed',
        subtitle: `${firstName} has been confirmed to fill the shift`,
        badgeIcon: <DoneIcon />,
        badgeColor: styles.primaryGreen500,
    },
    BOOKING_CONFIRMED: {
        title: 'Booking Confirmed',
        subtitle: `${firstName} has been confirmed by someone`,
        badgeIcon: <DoneIcon />,
        badgeColor: styles.primaryGreen500,
    },
})

export const SchoolEventSnackbar = ({
    data: { teacherUid, date, type, author },
}: {
    data: SchoolEventEntity
}) => {
    const { data: profilePicture, isPending: profilePicturePending } =
        useTeacherPublicInfoField<string>(teacherUid, 'profilePicture')

    const { data: firstName } = useTeacherPublicInfoField<string>(
        teacherUid,
        'firstName'
    )

    const config = useMemo(() => {
        let name: string
        switch (type) {
            case 'BOOKING_CANCELLED_REPLACING':
                name = ''
                break
            case 'BOOKING_CANCELLED_BY_SCHOOL':
                name = author?.name || ''
                break

            case 'BOOKING_CONFIRMED':
            case 'BOOKING_REPLACED':
            case 'BOOKING_CANCELLED_BY_TEACHER':
            default: {
                name = firstName || ''
            }
        }
        return notificationItemMap(name)[type]
    }, [type, firstName, author?.name])

    return (
        <Stack direction={'row'} alignItems={'center'} gap={1.5}>
            {profilePicturePending ? (
                <Skeleton variant="circular">
                    <Avatar sx={{ width: 80, height: 80 }} />
                </Skeleton>
            ) : (
                <Box sx={{ position: 'relative' }}>
                    <Avatar
                        sx={{ width: 80, height: 80 }}
                        src={profilePicture || ''}
                        alt={'Cancelled teacher'}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'absolute',
                            bottom: -10,
                            right: -10,
                            p: 1,
                            borderRadius: '50%',
                            backgroundColor: config.badgeColor,
                            aspectRatio: '1/1',
                            '& .MuiSvgIcon-root': {
                                height: 24,
                                width: 24,
                            },
                        }}
                    >
                        {config.badgeIcon}
                    </Box>
                </Box>
            )}
            <Stack>
                <Typography variant="h6">
                    <span style={{ fontWeight: 600 }}>{date}</span> -{' '}
                    {config.title}
                </Typography>
                <Typography>{config.subtitle}</Typography>
            </Stack>
        </Stack>
    )
}

export const SchoolEventActions = ({
    args: { date, teacherUid, placementUid, interest, id: eventUid, type },
    snackbarId,
}: {
    args: SchoolEventEntity
    snackbarId: any
}) => {
    const dispatch = useDispatch()
    const schoolUid = useSelector(selectActiveOrgId)
    const userUid = useSelector(selectUserUid)

    const schoolKey = useMemo(
        () => `${reverseDate(date)}-${teacherUid}`,
        [date, teacherUid]
    )

    const viewedSelector = useMemo(
        () => makeSelectHasMobileViewedBooking(schoolKey),
        [schoolKey]
    )

    const mobileViewed = useSelector((state: RootState) =>
        userUid ? viewedSelector(state, userUid, type) : false
    )

    const { closeSnackbar } = useSnackbar()

    const hasClosed = useRef(false)
    useEffect(() => {
        try {
            if (hasClosed.current || userUid === undefined || !schoolUid) return
            if (mobileViewed) {
                hasClosed.current = true
                markAsRead({ userUid, schoolUid, eventUid })
                closeSnackbar(snackbarId)
            }
        } catch (error) {}
    }, [mobileViewed, userUid, schoolUid, eventUid, closeSnackbar, snackbarId])

    return (
        <Stack direction={'row'} alignItems={'center'} gap={1}>
            <IconButton
                onClick={() => {
                    closeSnackbar(snackbarId)
                    markAsRead({ userUid, schoolUid, eventUid })
                }}
            >
                <CloseIcon
                    sx={{
                        color: styles.shades0,
                    }}
                />
            </IconButton>
            <Divider
                flexItem
                sx={{
                    borderColor: styles.shades0,
                    borderWidth: '1px',
                    minHeight: '12px',
                }}
                orientation="vertical"
            />
            <IconButton
                onClick={() => {
                    closeSnackbar(snackbarId)
                    dispatch(
                        pushModal({
                            key: 'bookingDetails',
                            payload: {
                                date,
                                placementUid,
                                teacherUid,
                                interest,
                            },
                        })
                    )
                    markAsRead({ userUid, schoolUid, eventUid })
                }}
            >
                {/* prettier-ignore */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/> </svg>
            </IconButton>
        </Stack>
    )
}

const markAsRead = ({
    schoolUid,
    userUid,
    eventUid,
}: {
    schoolUid?: string | null
    userUid?: string | null
    eventUid: string
}) => {
    requestIdleCallback(async () => {
        logger.debug(`Running markAsRead in idle callback...`)
        try {
            if (!schoolUid || !userUid) throw new Error('Invalid config')
            await SCHOOLS_API.eventReads.markAsRead(schoolUid, userUid, [
                eventUid,
            ])
        } catch (e: any) {
            logger.warn(
                `Failed to mark off notification - ${e?.message || 'unknown'}`
            )
        }
    })
}
